import { type Asset } from 'contentful'

import { assetIsSvg } from '@lib/utils/image'
import { objectKeys } from '@lib/utils/object'

import { Breakpoints, ContentMaxWidths } from '@constants/enums/layout'

import { updateAssetCDNUrl } from '@components/asset/utils'
import type { LayoutColumn } from '@components/layout/types'

type Format = 'original' | 'avif' | 'webp'

export type Sources = {
  [key in Format]: string[]
}

/**
 * Get media width based on breakpoint width.
 * @param {LayoutColumn} cols - Number of columns the media extends.
 */
const getBreakpointMediaWidths = (cols: LayoutColumn) => ({
  [Breakpoints.DesktopWide]: (ContentMaxWidths.DesktopWide / 12) * cols,
  [Breakpoints.Desktop]: (ContentMaxWidths.Desktop / 12) * cols,
  [Breakpoints.TabletWide]: ContentMaxWidths.TabletWide,
  [Breakpoints.Tablet]: ContentMaxWidths.Tablet,
  [Breakpoints.Mobile]: ContentMaxWidths.Mobile,
})

/**
 * Get all widths based on density and column width.
 * @param {LayoutColumn} cols - Number of columns the media extends.
 * @returns {number[]}
 */
const getMediaWidths = (cols: LayoutColumn): number[] => {
  const breakpointMediaWidths = getBreakpointMediaWidths(cols)
  const pixelDensities = [1, 2, 3]
  const widths: number[] = []
  for (const breakpoint of objectKeys(breakpointMediaWidths)) {
    for (const density of pixelDensities) {
      widths.push(breakpointMediaWidths[breakpoint] * density)
    }
  }
  return [...new Set(widths)].sort((a, b) => a - b)
}

/**
 * Get srcSet based on url and number of columns.
 * @param {string} url - Image url.
 * @param {LayoutColumn} cols - Number of columns the media extends.
 * @returns {Sources}
 */
export const getSrcSets = (url: string, cols: LayoutColumn): Sources => {
  const sources: Sources = {
    avif: [],
    webp: [],
    original: [],
  }
  const formats = objectKeys(sources)
  const widths = getMediaWidths(cols)

  for (const format of formats) {
    if (!sources[format]) {
      sources[format] = []
    }
    for (const width of widths) {
      if (url) {
        const formatParam = format == 'original' ? '' : `&fm=${format}`
        sources[format].push(`${url}?w=${width}` + formatParam + ` ${width}w`)
      }
    }
  }

  return sources
}

/**
 * Get sizes attribute for <picture> or <img> based on columns.
 * @param {LayoutColumn} cols - Number of columns the media extends.
 * @returns {string[]}
 */
export const getSizes = (cols: LayoutColumn): string[] => {
  return [
    '(max-width: 1024px) 100vw',
    `(min-width: 1280px) ${(ContentMaxWidths.Desktop / 12) * cols}px`,
    `(min-width: 1440px) ${(ContentMaxWidths.DesktopWide / 12) * cols}px`,
    '100vw', // Fallback size
  ]
}

/**
 * Get largest natural width. Used for settings width in HTML
 * and fallback width.
 * @param {Column} cols - Number of columns the media extends.
 * @returns {number}
 */
export const getLargestWidth = (cols: LayoutColumn): number => {
  const mediaWidth = getBreakpointMediaWidths(cols)
  return Math.max(...Object.values(mediaWidth))
}

/**
 * Get fallback src for <img>.
 * @param {string} url - Image url.
 * @param {Column} cols - Number of columns the media extends.
 * @returns {number}
 */
export const getFallbackSrc = (url: string, cols: LayoutColumn): string =>
  url ? `${url}?w=${getLargestWidth(cols)}` : ''

/**
 * Extract key properties from a Contentful asset with safe defaults.
 * @param {Asset} asset - Contentful asset object.
 * @returns {object} Normalized asset properties with id, alt, url, dimensions, and SVG flag.
 */
export function getContentfulAssetProps(
  asset: Asset<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'> | undefined,
) {
  const id = asset?.sys?.id ?? ''
  const alt = asset?.fields?.description ?? ''
  const url = updateAssetCDNUrl(asset?.fields?.file?.url ?? '')
  const width = asset?.fields?.file?.details?.image?.width ?? 0
  const height = asset?.fields?.file?.details?.image?.height ?? 0
  const isSvg = assetIsSvg(asset?.fields?.file?.contentType)

  return {
    id,
    alt,
    url,
    width,
    height,
    isSvg,
  }
}

type PictureAttributesParams = {
  width: number
  height: number
  url: string
  isSvg: boolean
  cols: LayoutColumn
}

export type PictureAttributes = {
  src: string
  width: number
  height: number
  srcSets: Sources | undefined
  isSvg: boolean
  sizes: string[]
}

/**
 * Calculate all attributes needed for a picture element with responsive sources.
 * @param {object} params - Image parameters.
 * @param {number} params.width - Original image width.
 * @param {number} params.height - Original image height.
 * @param {string} params.url - Image URL.
 * @param {boolean} params.isSvg - Whether the image is an SVG.
 * @param {LayoutColumn} params.cols - Number of columns the image spans.
 * @returns {object} Picture attributes including src, dimensions, srcSets, and sizes.
 */
export function getPictureAttributes({
  width,
  height,
  url,
  isSvg,
  cols,
}: PictureAttributesParams): PictureAttributes {
  const src = isSvg ? url : getFallbackSrc(url, cols)

  const aspectRatio = height > 0 ? width / height : 1
  const largestWidth = getLargestWidth(cols)
  const _width = isSvg ? width : Math.min(largestWidth, width)
  const _height = isSvg ? height : _width / aspectRatio

  const srcSets = isSvg || !url ? undefined : getSrcSets(url, cols)

  const sizes = getSizes(cols)

  return {
    src,
    width: _width,
    height: _height,
    srcSets,
    isSvg,
    sizes,
  }
}
